import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConversionNudge } from "@/contexts/ConversionNudgeContext";
import { trackClick } from "@/lib/track";

/**
 * Subtle, dismissible "Subscribe to unlock the full catalog" nudge. Shown once per
 * session (coordinated by ConversionNudgeContext) after a non-subscriber finishes a
 * public track/video or views a public image. Auto-hides after 12s.
 */
export default function ConvertNudge() {
  const { showConvertNudge, dismissConvertNudge } = useConversionNudge();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!showConvertNudge) return;
    const id = setTimeout(dismissConvertNudge, 12000);
    return () => clearTimeout(id);
  }, [showConvertNudge, dismissConvertNudge]);

  return (
    <AnimatePresence>
      {showConvertNudge && (
        <motion.div
          data-testid="convert-nudge"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35 }}
          className="fixed z-[10000] left-1/2 -translate-x-1/2 bottom-28 sm:bottom-24 w-[92%] max-w-md"
        >
          <div
            className="flex items-center gap-3 rounded-2xl px-4 py-3 shadow-xl"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-color)",
              backdropFilter: "blur(12px)",
            }}
          >
            <span
              className="flex items-center justify-center w-9 h-9 rounded-full shrink-0"
              style={{ background: "var(--accent-primary)", color: "#fff" }}
            >
              <Sparkles className="w-4 h-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight" style={{ color: "var(--text-primary)" }}>
                {t("commerce.nudgeTitle")}
              </p>
              <p className="text-xs leading-tight" style={{ color: "var(--text-secondary)" }}>
                {t("commerce.nudgeBody")}
              </p>
            </div>
            <button
              data-testid="convert-nudge-cta"
              onClick={() => { trackClick("subscribe_cta", { source: "convert_nudge" }); dismissConvertNudge(); navigate("/pricing"); }}
              className="shrink-0 text-xs font-bold px-4 py-2 rounded-full"
              style={{ background: "var(--accent-primary)", color: "#fff" }}
            >
              {t("commerce.subscribe")}
            </button>
            <button
              data-testid="convert-nudge-dismiss"
              onClick={dismissConvertNudge}
              aria-label={t("commerce.dismiss")}
              className="shrink-0 p-1 rounded-full"
              style={{ color: "var(--text-secondary)" }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
